import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { 
  Plus, 
  Edit2, 
  Trash2, 
  GripVertical, 
  Search,
  FileText,
  Building2,
  Home,
  Briefcase,
  CreditCard,
  Car,
  GraduationCap,
  Save,
  X,
  ArrowUp,
  ArrowDown,
  Settings,
  AlertCircle,
  Loader2,
  FileCheck,
  ClipboardList,
  Import,
  FilePlus,
  BookCopy,
  Send,
  FileEdit,
  LayoutTemplate
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { 
  collection, 
  getDocs, 
  doc, 
  setDoc, 
  updateDoc, 
  deleteDoc,
  Timestamp
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { 
  QuestionnaireTemplate, 
  QuestionnaireForm, 
  TemplateSection, 
  TemplateField,
  LoanType 
} from "@/types/questionnaire";
import { migrateQuestionnaireTemplates } from "@/utils/migrateQuestionnaireTemplates";
import { useIsMobile } from "@/hooks/use-mobile";
import { lenders } from "@/data/payoutsData";

// Loan type display names and icons
const LOAN_TYPES: { id: LoanType; name: string; icon: React.ElementType }[] = [
  { id: 'home_loan', name: 'Home Loan', icon: Home },
  { id: 'lap', name: 'Loan Against Property', icon: Building2 },
  { id: 'personal_loan', name: 'Personal Loan', icon: CreditCard },
  { id: 'business_loan', name: 'Business Loan', icon: Briefcase },
  { id: 'car_loan', name: 'Car Loan', icon: Car },
  { id: 'education_loan', name: 'Education Loan', icon: GraduationCap },
];

// Field type options
const FIELD_TYPES = [
  { value: 'text', label: 'Text Input' },
  { value: 'number', label: 'Number' },
  { value: 'currency', label: 'Currency (₹)' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'select', label: 'Dropdown' },
  { value: 'multi_select', label: 'Multi-Select' },
  { value: 'radio', label: 'Radio Buttons' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'date', label: 'Date Picker' },
  { value: 'pan', label: 'PAN Card' },
  { value: 'aadhaar', label: 'Aadhaar' },
];

// Section icon options
const SECTION_ICONS = [
  'Home', 'Building2', 'Users', 'Briefcase', 'CreditCard', 'Building', 
  'FileCheck', 'Target', 'TrendingUp', 'Sparkles', 'User', 'GraduationCap', 
  'BookOpen', 'Award', 'Car', 'HelpCircle', 'ClipboardList', 'FileText'
];

// Get unique lenders from payouts data
const AVAILABLE_LENDERS = lenders.map(lender => ({
  id: lender.toLowerCase().replace(/\s+/g, '_'),
  name: lender
}));

export function QuestionnaireManagement() {
  const isMobile = useIsMobile();
  const [activeSubTab, setActiveSubTab] = useState<'templates' | 'lender-forms'>('templates');
  const [templates, setTemplates] = useState<QuestionnaireTemplate[]>([]);
  const [lenderForms, setLenderForms] = useState<QuestionnaireForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLoanType, setSelectedLoanType] = useState<LoanType | 'all'>('all');
  
  // Editor state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<QuestionnaireTemplate | null>(null);
  const [editingLenderForm, setEditingLenderForm] = useState<QuestionnaireForm | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // Create lender form dialog state
  const [createFormDialogOpen, setCreateFormDialogOpen] = useState(false);
  const [newFormLoanType, setNewFormLoanType] = useState<LoanType>('home_loan');
  const [newFormLender, setNewFormLender] = useState<string>('');
  const [newFormMode, setNewFormMode] = useState<'fresh' | 'import'>('import');
  
  // Migration state
  const [isMigrating, setIsMigrating] = useState(false);
  const [showMigrationDialog, setShowMigrationDialog] = useState(false);

  // Load templates and lender forms from Firebase
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load base templates
      const templatesSnap = await getDocs(collection(db, 'questionnaire_templates'));
      const loadedTemplates: QuestionnaireTemplate[] = [];
      templatesSnap.forEach((docSnap) => {
        loadedTemplates.push({ id: docSnap.id, ...docSnap.data() } as QuestionnaireTemplate);
      });
      setTemplates(loadedTemplates);

      // Load lender-specific forms
      const formsSnap = await getDocs(collection(db, 'questionnaire_forms'));
      const loadedForms: QuestionnaireForm[] = [];
      formsSnap.forEach((docSnap) => {
        loadedForms.push({ id: docSnap.id, ...docSnap.data() } as QuestionnaireForm);
      });
      setLenderForms(loadedForms);
    } catch (error) {
      console.error('Error loading questionnaire data:', error);
      toast.error('Failed to load questionnaire data');
    } finally {
      setLoading(false);
    }
  };

  // Run migration to populate base templates
  const handleMigration = async () => {
    setIsMigrating(true);
    try {
      const result = await migrateQuestionnaireTemplates();
      if (result.success) {
        toast.success(`Migration complete: ${result.templatesCreated} templates created`);
        await loadData();
      } else {
        toast.error('Migration failed: ' + result.error);
      }
    } catch (error) {
      console.error('Migration error:', error);
      toast.error('Migration failed');
    } finally {
      setIsMigrating(false);
      setShowMigrationDialog(false);
    }
  };

  // Filter templates based on search and loan type
  const filteredTemplates = useMemo(() => templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          template.loanType.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesLoanType = selectedLoanType === 'all' || template.loanType === selectedLoanType;
    return matchesSearch && matchesLoanType;
  }), [templates, searchQuery, selectedLoanType]);

  // Filter lender forms based on search and loan type
  const filteredLenderForms = useMemo(() => lenderForms.filter(form => {
    const matchesSearch = form.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          form.lenderName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          form.loanType.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesLoanType = selectedLoanType === 'all' || form.loanType === selectedLoanType;
    return matchesSearch && matchesLoanType;
  }), [lenderForms, searchQuery, selectedLoanType]);

  // Open template editor
  const handleEditTemplate = (template: QuestionnaireTemplate) => {
    setEditingTemplate(template);
    setEditingLenderForm(null);
    setEditorOpen(true);
  };

  // Open lender form editor
  const handleEditLenderForm = (form: QuestionnaireForm) => {
    setEditingLenderForm(form);
    setEditingTemplate(null);
    setEditorOpen(true);
  };

  // Create new template
  const handleCreateTemplate = () => {
    const newTemplate: QuestionnaireTemplate = {
      id: '',
      loanType: 'home_loan',
      name: 'New Template',
      description: 'Enter template description',
      version: 1,
      isActive: true,
      sections: [],
      requiredDocuments: [],
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      createdBy: 'admin',
    };
    setEditingTemplate(newTemplate);
    setEditingLenderForm(null);
    setEditorOpen(true);
  };

  // Create new lender-specific form
  const handleCreateLenderForm = () => {
    setNewFormLoanType('home_loan');
    setNewFormLender('');
    setNewFormMode('import');
    setCreateFormDialogOpen(true);
  };

  // Confirm creation of lender form
  const handleConfirmCreateLenderForm = async () => {
    if (!newFormLender) {
      toast.error('Please select a lender');
      return;
    }

    const lenderInfo = AVAILABLE_LENDERS.find(l => l.id === newFormLender);
    if (!lenderInfo) {
      toast.error('Invalid lender selected');
      return;
    }

    // Check if form already exists for this combination
    const existingForm = lenderForms.find(
      f => f.loanType === newFormLoanType && f.lenderId === newFormLender
    );
    if (existingForm) {
      toast.error('A form already exists for this lender and loan type combination');
      return;
    }

    let newForm: QuestionnaireForm;
    
    if (newFormMode === 'import') {
      // Import from base template
      const baseTemplate = templates.find(t => t.loanType === newFormLoanType);
      if (!baseTemplate) {
        toast.error('No base template found for this loan type. Please initialize templates first.');
        return;
      }

      newForm = {
        id: `${newFormLoanType}_${newFormLender}`,
        loanType: newFormLoanType,
        lenderId: newFormLender,
        lenderName: lenderInfo.name,
        baseTemplateId: baseTemplate.id,
        name: `${lenderInfo.name} - ${LOAN_TYPES.find(lt => lt.id === newFormLoanType)?.name}`,
        description: `Questionnaire form for ${lenderInfo.name}`,
        sections: JSON.parse(JSON.stringify(baseTemplate.sections)),
        version: 1,
        isActive: true,
        status: 'draft',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        createdBy: 'admin',
      };
    } else {
      // Create fresh/empty form
      newForm = {
        id: `${newFormLoanType}_${newFormLender}`,
        loanType: newFormLoanType,
        lenderId: newFormLender,
        lenderName: lenderInfo.name,
        baseTemplateId: newFormLoanType,
        name: `${lenderInfo.name} - ${LOAN_TYPES.find(lt => lt.id === newFormLoanType)?.name}`,
        description: `Questionnaire form for ${lenderInfo.name}`,
        sections: [],
        version: 1,
        isActive: true,
        status: 'draft',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        createdBy: 'admin',
      };
    }

    setCreateFormDialogOpen(false);
    setEditingLenderForm(newForm);
    setEditingTemplate(null);
    setEditorOpen(true);
  };

  // Delete template
  const handleDeleteTemplate = async (templateId: string) => {
    if (!confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await deleteDoc(doc(db, 'questionnaire_templates', templateId));
      toast.success('Template deleted');
      await loadData();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    }
  };

  // Delete lender form
  const handleDeleteLenderForm = async (formId: string) => {
    if (!confirm('Are you sure you want to delete this lender form?')) return;
    
    try {
      await deleteDoc(doc(db, 'questionnaire_forms', formId));
      toast.success('Lender form deleted');
      await loadData();
    } catch (error) {
      console.error('Error deleting lender form:', error);
      toast.error('Failed to delete lender form');
    }
  };

  // Save template
  const handleSaveTemplate = async (template: QuestionnaireTemplate) => {
    setIsSaving(true);
    try {
      const docId = template.id || template.loanType;
      const templateData = {
        ...template,
        id: docId,
        version: (template.version || 0) + 1,
        updatedAt: Timestamp.now(),
      };
      
      await setDoc(doc(db, 'questionnaire_templates', docId), templateData);
      toast.success('Template saved successfully');
      setEditorOpen(false);
      await loadData();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    } finally {
      setIsSaving(false);
    }
  };

  // Save lender form
  const handleSaveLenderForm = async (form: QuestionnaireForm) => {
    setIsSaving(true);
    try {
      const docId = form.id || `${form.loanType}_${form.lenderId}`;
      const formData = {
        ...form,
        id: docId,
        version: (form.version || 0) + 1,
        updatedAt: Timestamp.now(),
      };
      
      await setDoc(doc(db, 'questionnaire_forms', docId), formData);
      toast.success('Lender form saved successfully');
      setEditorOpen(false);
      await loadData();
    } catch (error) {
      console.error('Error saving lender form:', error);
      toast.error('Failed to save lender form');
    } finally {
      setIsSaving(false);
    }
  };

  // Publish lender form
  const handlePublishLenderForm = async (form: QuestionnaireForm) => {
    setIsSaving(true);
    try {
      const docId = form.id;
      await updateDoc(doc(db, 'questionnaire_forms', docId), {
        status: 'published',
        version: (form.version || 0) + 1,
        updatedAt: Timestamp.now(),
      });
      toast.success('Form published! It will now be used for new leads.');
      setEditorOpen(false);
      await loadData();
    } catch (error) {
      console.error('Error publishing form:', error);
      toast.error('Failed to publish form');
    } finally {
      setIsSaving(false);
    }
  };

  // Duplicate template as lender form
  const handleDuplicateAsLenderForm = (template: QuestionnaireTemplate) => {
    setNewFormLoanType(template.loanType);
    setNewFormLender('');
    setNewFormMode('import');
    setCreateFormDialogOpen(true);
  };

  // Get loan type info
  const getLoanTypeInfo = (loanType: LoanType) => {
    return LOAN_TYPES.find(lt => lt.id === loanType) || LOAN_TYPES[0];
  };

  // Render loading skeleton
  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} className="h-40" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-primary" />
            Questionnaire Forms
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Manage questionnaire templates and lender-specific forms
          </p>
        </div>
        
        <div className="flex gap-2 flex-wrap">
          {templates.length === 0 && (
            <Button 
              variant="outline" 
              onClick={() => setShowMigrationDialog(true)}
              disabled={isMigrating}
            >
              {isMigrating ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <FileCheck className="h-4 w-4 mr-2" />
              )}
              Initialize Templates
            </Button>
          )}
          
          {activeSubTab === 'templates' ? (
            <Button onClick={handleCreateTemplate}>
              <Plus className="h-4 w-4 mr-2" />
              New Template
            </Button>
          ) : (
            <Button onClick={handleCreateLenderForm}>
              <Plus className="h-4 w-4 mr-2" />
              New Lender Form
            </Button>
          )}
        </div>
      </div>

      {/* Sub-tabs */}
      <Tabs value={activeSubTab} onValueChange={(v) => setActiveSubTab(v as 'templates' | 'lender-forms')}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="templates" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <LayoutTemplate className="h-4 w-4 mr-2" />
            Base Templates ({templates.length})
          </TabsTrigger>
          <TabsTrigger value="lender-forms" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Building2 className="h-4 w-4 mr-2" />
            Lender Forms ({lenderForms.length})
          </TabsTrigger>
        </TabsList>

        {/* Filters */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center mt-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          
          <Select value={selectedLoanType} onValueChange={(v) => setSelectedLoanType(v as LoanType | 'all')}>
            <SelectTrigger className="w-full sm:w-48">
              <SelectValue placeholder="Filter by loan type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Loan Types</SelectItem>
              {LOAN_TYPES.map((lt) => (
                <SelectItem key={lt.id} value={lt.id}>
                  <div className="flex items-center gap-2">
                    <lt.icon className="h-4 w-4" />
                    {lt.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Base Templates Tab */}
        <TabsContent value="templates" className="mt-4">
          {filteredTemplates.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <FileText className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="font-semibold text-lg mb-2">No Templates Found</h3>
                <p className="text-muted-foreground text-center max-w-md mb-4">
                  {templates.length === 0 
                    ? "Initialize base templates from existing questionnaire configuration to get started."
                    : "No templates match your search criteria."}
                </p>
                {templates.length === 0 && (
                  <Button onClick={() => setShowMigrationDialog(true)}>
                    <FileCheck className="h-4 w-4 mr-2" />
                    Initialize Templates
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filteredTemplates.map((template) => {
                const loanTypeInfo = getLoanTypeInfo(template.loanType);
                return (
                  <Card key={template.id} className="group hover:shadow-md transition-shadow">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                            <loanTypeInfo.icon className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <CardTitle className="text-base">{template.name}</CardTitle>
                            <CardDescription className="text-xs mt-0.5">
                              {loanTypeInfo.name} • v{template.version}
                            </CardDescription>
                          </div>
                        </div>
                        <Badge variant={template.isActive ? "default" : "secondary"}>
                          {template.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="space-y-3">
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span>{template.sections?.length || 0} sections</span>
                          <span>•</span>
                          <span>
                            {template.sections?.reduce((acc, s) => acc + (s.fields?.length || 0), 0) || 0} fields
                          </span>
                        </div>
                        
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="flex-1"
                            onClick={() => handleEditTemplate(template)}
                          >
                            <Edit2 className="h-3.5 w-3.5 mr-1.5" />
                            Edit
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleDuplicateAsLenderForm(template)}
                            title="Create lender form from this template"
                          >
                            <BookCopy className="h-3.5 w-3.5" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleDeleteTemplate(template.id)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Lender Forms Tab */}
        <TabsContent value="lender-forms" className="mt-4">
          {filteredLenderForms.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Building2 className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="font-semibold text-lg mb-2">No Lender Forms Found</h3>
                <p className="text-muted-foreground text-center max-w-md mb-4">
                  Create lender-specific forms by selecting a lender and loan type combination.
                  You can start fresh or import from a base template.
                </p>
                <Button onClick={handleCreateLenderForm}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Lender Form
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filteredLenderForms.map((form) => {
                const loanTypeInfo = getLoanTypeInfo(form.loanType);
                const statusColor = form.status === 'published' ? 'default' : 'secondary';
                return (
                  <Card key={form.id} className="group hover:shadow-md transition-shadow">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-950">
                            <Building2 className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <CardTitle className="text-base">{form.lenderName || form.name}</CardTitle>
                            <CardDescription className="text-xs mt-0.5">
                              {loanTypeInfo.name} • v{form.version}
                            </CardDescription>
                          </div>
                        </div>
                        <div className="flex flex-col gap-1 items-end">
                          <Badge variant={statusColor}>
                            {form.status === 'published' ? 'Published' : 'Draft'}
                          </Badge>
                          {!form.isActive && (
                            <Badge variant="outline" className="text-xs">Inactive</Badge>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="space-y-3">
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span>{form.sections?.length || 0} sections</span>
                          <span>•</span>
                          <span>
                            {form.sections?.reduce((acc, s) => acc + (s.fields?.length || 0), 0) || 0} fields
                          </span>
                        </div>
                        
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="flex-1"
                            onClick={() => handleEditLenderForm(form)}
                          >
                            <Edit2 className="h-3.5 w-3.5 mr-1.5" />
                            Edit
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleDeleteLenderForm(form.id)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Lender Form Dialog */}
      <Dialog open={createFormDialogOpen} onOpenChange={setCreateFormDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FilePlus className="h-5 w-5 text-primary" />
              Create Lender Form
            </DialogTitle>
            <DialogDescription>
              Create a questionnaire form specific to a lender and loan type combination.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Loan Type</Label>
              <Select value={newFormLoanType} onValueChange={(v) => setNewFormLoanType(v as LoanType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LOAN_TYPES.map((lt) => (
                    <SelectItem key={lt.id} value={lt.id}>
                      <div className="flex items-center gap-2">
                        <lt.icon className="h-4 w-4" />
                        {lt.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Lender / Bank</Label>
              <Select value={newFormLender} onValueChange={setNewFormLender}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a lender" />
                </SelectTrigger>
                <SelectContent>
                  {AVAILABLE_LENDERS.map((lender) => {
                    const exists = lenderForms.some(
                      f => f.loanType === newFormLoanType && f.lenderId === lender.id
                    );
                    return (
                      <SelectItem 
                        key={lender.id} 
                        value={lender.id}
                        disabled={exists}
                      >
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4" />
                          {lender.name}
                          {exists && <span className="text-xs text-muted-foreground">(exists)</span>}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <Separator />

            <div className="space-y-3">
              <Label>Start From</Label>
              
              <div className="grid grid-cols-2 gap-3">
                <Card 
                  className={cn(
                    "cursor-pointer transition-all p-3",
                    newFormMode === 'import' 
                      ? "border-primary ring-2 ring-primary/20" 
                      : "hover:border-muted-foreground/50"
                  )}
                  onClick={() => setNewFormMode('import')}
                >
                  <div className="flex flex-col items-center text-center gap-2">
                    <Import className={cn("h-8 w-8", newFormMode === 'import' ? "text-primary" : "text-muted-foreground")} />
                    <div>
                      <p className="font-medium text-sm">Import Template</p>
                      <p className="text-xs text-muted-foreground">Copy from base template</p>
                    </div>
                  </div>
                </Card>
                
                <Card 
                  className={cn(
                    "cursor-pointer transition-all p-3",
                    newFormMode === 'fresh' 
                      ? "border-primary ring-2 ring-primary/20" 
                      : "hover:border-muted-foreground/50"
                  )}
                  onClick={() => setNewFormMode('fresh')}
                >
                  <div className="flex flex-col items-center text-center gap-2">
                    <FilePlus className={cn("h-8 w-8", newFormMode === 'fresh' ? "text-primary" : "text-muted-foreground")} />
                    <div>
                      <p className="font-medium text-sm">Fresh Start</p>
                      <p className="text-xs text-muted-foreground">Empty form</p>
                    </div>
                  </div>
                </Card>
              </div>

              {newFormMode === 'import' && (
                <div className="flex items-center gap-2 p-2 bg-muted rounded-lg text-sm">
                  <FileCheck className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">
                    Will copy sections and fields from: <strong>{LOAN_TYPES.find(lt => lt.id === newFormLoanType)?.name}</strong> base template
                  </span>
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateFormDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmCreateLenderForm} disabled={!newFormLender}>
              <Plus className="h-4 w-4 mr-2" />
              Create Form
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Migration Confirmation Dialog */}
      <Dialog open={showMigrationDialog} onOpenChange={setShowMigrationDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Initialize Questionnaire Templates</DialogTitle>
            <DialogDescription>
              This will create base questionnaire templates for all loan types from the existing static configuration.
              This is a one-time migration to populate Firebase with initial data.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="flex items-center gap-3 p-3 bg-amber-50 dark:bg-amber-950 rounded-lg">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              <p className="text-sm text-amber-800 dark:text-amber-200">
                Existing templates with the same loan type will be overwritten.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMigrationDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleMigration} disabled={isMigrating}>
              {isMigrating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Migrating...
                </>
              ) : (
                <>
                  <FileCheck className="h-4 w-4 mr-2" />
                  Initialize Templates
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template/Form Editor - Uses Sheet for mobile, Dialog for desktop */}
      {isMobile ? (
        <FormEditorSheet
          open={editorOpen}
          onOpenChange={setEditorOpen}
          template={editingTemplate}
          lenderForm={editingLenderForm}
          onSaveTemplate={handleSaveTemplate}
          onSaveLenderForm={handleSaveLenderForm}
          onPublishLenderForm={handlePublishLenderForm}
          isSaving={isSaving}
        />
      ) : (
        <FormEditorDialog
          open={editorOpen}
          onOpenChange={setEditorOpen}
          template={editingTemplate}
          lenderForm={editingLenderForm}
          onSaveTemplate={handleSaveTemplate}
          onSaveLenderForm={handleSaveLenderForm}
          onPublishLenderForm={handlePublishLenderForm}
          isSaving={isSaving}
        />
      )}
    </div>
  );
}

// ============================================================================
// FORM EDITOR COMPONENTS
// ============================================================================

interface FormEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: QuestionnaireTemplate | null;
  lenderForm: QuestionnaireForm | null;
  onSaveTemplate: (template: QuestionnaireTemplate) => void;
  onSaveLenderForm: (form: QuestionnaireForm) => void;
  onPublishLenderForm: (form: QuestionnaireForm) => void;
  isSaving: boolean;
}

// Form Editor Content - Shared between Sheet and Dialog
function FormEditorContent({ 
  template, 
  lenderForm, 
  onSaveTemplate, 
  onSaveLenderForm,
  onPublishLenderForm,
  isSaving,
  onClose
}: FormEditorProps & { onClose: () => void }) {
  const isTemplate = !!template;
  const [editedTemplate, setEditedTemplate] = useState<QuestionnaireTemplate | null>(null);
  const [editedForm, setEditedForm] = useState<QuestionnaireForm | null>(null);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  useEffect(() => {
    if (template) {
      setEditedTemplate({ ...template });
      setEditedForm(null);
    } else if (lenderForm) {
      setEditedForm({ ...lenderForm });
      setEditedTemplate(null);
    }
  }, [template, lenderForm]);

  const editedData = isTemplate ? editedTemplate : editedForm;
  if (!editedData) return null;

  const sections = isTemplate 
    ? (editedTemplate?.sections || [])
    : (editedForm?.sections || []);

  const updateField = (field: string, value: unknown) => {
    if (isTemplate) {
      setEditedTemplate(prev => prev ? { ...prev, [field]: value } : null);
    } else {
      setEditedForm(prev => prev ? { ...prev, [field]: value } : null);
    }
  };

  const addSection = () => {
    const newSection: TemplateSection = {
      id: `section_${Date.now()}`,
      title: 'New Section',
      description: '',
      iconName: 'FileText',
      order: sections.length,
      fields: [],
    };
    
    if (isTemplate) {
      setEditedTemplate(prev => prev ? {
        ...prev,
        sections: [...(prev.sections || []), newSection]
      } : null);
    } else {
      setEditedForm(prev => prev ? {
        ...prev,
        sections: [...(prev.sections || []), newSection]
      } : null);
    }
    setExpandedSection(newSection.id);
  };

  const updateSection = (sectionId: string, updates: Partial<TemplateSection>) => {
    if (isTemplate) {
      setEditedTemplate(prev => {
        if (!prev) return null;
        return {
          ...prev,
          sections: prev.sections.map(s => 
            s.id === sectionId ? { ...s, ...updates } : s
          )
        };
      });
    } else {
      setEditedForm(prev => {
        if (!prev) return null;
        return {
          ...prev,
          sections: (prev.sections || []).map(s => 
            s.id === sectionId ? { ...s, ...updates } : s
          )
        };
      });
    }
  };

  const deleteSection = (sectionId: string) => {
    if (!confirm('Delete this section and all its fields?')) return;
    if (isTemplate) {
      setEditedTemplate(prev => {
        if (!prev) return null;
        return {
          ...prev,
          sections: prev.sections.filter(s => s.id !== sectionId)
        };
      });
    } else {
      setEditedForm(prev => {
        if (!prev) return null;
        return {
          ...prev,
          sections: (prev.sections || []).filter(s => s.id !== sectionId)
        };
      });
    }
  };

  const moveSection = (sectionId: string, direction: 'up' | 'down') => {
    const setter = isTemplate ? setEditedTemplate : setEditedForm;
    setter((prev: QuestionnaireTemplate | QuestionnaireForm | null) => {
      if (!prev) return null;
      const sectionsList = [...(prev.sections || [])];
      const index = sectionsList.findIndex((s: TemplateSection) => s.id === sectionId);
      if (index < 0) return prev;
      
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= sectionsList.length) return prev;
      
      [sectionsList[index], sectionsList[newIndex]] = [sectionsList[newIndex], sectionsList[index]];
      return { ...prev, sections: sectionsList } as typeof prev;
    });
  };

  const addField = (sectionId: string) => {
    const newField: TemplateField = {
      id: `field_${Date.now()}`,
      type: 'text',
      label: 'New Field',
      placeholder: '',
      validation: { required: false },
      order: 0,
    };
    
    const section = sections.find(s => s.id === sectionId);
    if (section) {
      updateSection(sectionId, {
        fields: [...(section.fields || []), newField]
      });
    }
  };

  const updateFieldInSection = (sectionId: string, fieldId: string, updates: Partial<TemplateField>) => {
    const setter = isTemplate ? setEditedTemplate : setEditedForm;
    setter((prev: QuestionnaireTemplate | QuestionnaireForm | null) => {
      if (!prev) return null;
      return {
        ...prev,
        sections: (prev.sections || []).map((section: TemplateSection) => {
          if (section.id !== sectionId) return section;
          return {
            ...section,
            fields: (section.fields || []).map((field: TemplateField) =>
              field.id === fieldId ? { ...field, ...updates } : field
            )
          };
        })
      } as typeof prev;
    });
  };

  const deleteField = (sectionId: string, fieldId: string) => {
    const setter = isTemplate ? setEditedTemplate : setEditedForm;
    setter((prev: QuestionnaireTemplate | QuestionnaireForm | null) => {
      if (!prev) return null;
      return {
        ...prev,
        sections: (prev.sections || []).map((section: TemplateSection) => {
          if (section.id !== sectionId) return section;
          return {
            ...section,
            fields: (section.fields || []).filter((f: TemplateField) => f.id !== fieldId)
          };
        })
      } as typeof prev;
    });
  };

  const handleSave = () => {
    if (isTemplate && editedTemplate) {
      onSaveTemplate(editedTemplate);
    } else if (editedForm) {
      onSaveLenderForm(editedForm);
    }
  };

  const handlePublish = () => {
    if (editedForm) {
      onPublishLenderForm(editedForm);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <ScrollArea className="flex-1 px-6">
        <div className="py-4 space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="font-medium flex items-center gap-2">
              <Settings className="h-4 w-4" />
              {isTemplate ? 'Template Information' : 'Form Information'}
            </h3>
            
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={isTemplate ? editedTemplate?.name : editedForm?.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  placeholder="e.g., Home Loan Base Template"
                />
              </div>
              
              {isTemplate ? (
                <div className="space-y-2">
                  <Label htmlFor="loanType">Loan Type</Label>
                  <Select 
                    value={editedTemplate?.loanType} 
                    onValueChange={(v) => updateField('loanType', v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LOAN_TYPES.map((lt) => (
                        <SelectItem key={lt.id} value={lt.id}>
                          <div className="flex items-center gap-2">
                            <lt.icon className="h-4 w-4" />
                            {lt.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Lender</Label>
                  <div className="flex items-center gap-2 h-10 px-3 border rounded-md bg-muted">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <span>{editedForm?.lenderName}</span>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={isTemplate ? (editedTemplate?.description || '') : (editedForm?.description || '')}
                onChange={(e) => updateField('description', e.target.value)}
                placeholder="Brief description"
                rows={2}
              />
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  id="isActive"
                  checked={isTemplate ? editedTemplate?.isActive : editedForm?.isActive}
                  onCheckedChange={(checked) => updateField('isActive', checked)}
                />
                <Label htmlFor="isActive">Active</Label>
              </div>
              
              {!isTemplate && (
                <div className="flex items-center gap-2">
                  <Badge variant={editedForm?.status === 'published' ? 'default' : 'secondary'}>
                    {editedForm?.status === 'published' ? 'Published' : 'Draft'}
                  </Badge>
                  <span className="text-xs text-muted-foreground">v{editedForm?.version}</span>
                </div>
              )}
            </div>
          </div>

          <Separator />

          {/* Sections */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-medium flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Sections ({sections.length})
              </h3>
              <Button variant="outline" size="sm" onClick={addSection}>
                <Plus className="h-4 w-4 mr-1" />
                Add Section
              </Button>
            </div>

            {sections.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-8">
                  <FileText className="h-8 w-8 text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">No sections yet</p>
                  <Button variant="link" size="sm" onClick={addSection}>
                    Add your first section
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Accordion 
                type="single" 
                collapsible 
                value={expandedSection || undefined}
                onValueChange={(v) => setExpandedSection(v || null)}
              >
                {sections.map((section, index) => (
                  <AccordionItem key={section.id} value={section.id} className="border rounded-lg mb-2">
                    <AccordionTrigger className="px-4 hover:no-underline">
                      <div className="flex items-center gap-3 flex-1">
                        <GripVertical className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{section.title}</span>
                        <Badge variant="secondary" className="ml-auto mr-2">
                          {section.fields?.length || 0} fields
                        </Badge>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4">
                      <div className="space-y-4">
                        <div className="grid gap-3 sm:grid-cols-2">
                          <div className="space-y-2">
                            <Label>Section Title</Label>
                            <Input
                              value={section.title}
                              onChange={(e) => updateSection(section.id, { title: e.target.value })}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Icon</Label>
                            <Select 
                              value={section.iconName || 'FileText'} 
                              onValueChange={(v) => updateSection(section.id, { iconName: v })}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {SECTION_ICONS.map((icon) => (
                                  <SelectItem key={icon} value={icon}>
                                    {icon}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label>Description</Label>
                          <Input
                            value={section.description || ''}
                            onChange={(e) => updateSection(section.id, { description: e.target.value })}
                            placeholder="Optional section description"
                          />
                        </div>

                        <div className="flex gap-2 pt-2 border-t">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => moveSection(section.id, 'up')}
                            disabled={index === 0}
                          >
                            <ArrowUp className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => moveSection(section.id, 'down')}
                            disabled={index === sections.length - 1}
                          >
                            <ArrowDown className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => deleteSection(section.id)}
                            className="ml-auto text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>

                        <div className="space-y-3 pt-4 border-t">
                          <div className="flex items-center justify-between">
                            <Label>Fields</Label>
                            <Button variant="ghost" size="sm" onClick={() => addField(section.id)}>
                              <Plus className="h-4 w-4 mr-1" />
                              Add Field
                            </Button>
                          </div>

                          {section.fields?.length === 0 ? (
                            <p className="text-sm text-muted-foreground text-center py-4">
                              No fields yet. Click "Add Field" to add one.
                            </p>
                          ) : (
                            <div className="space-y-2">
                              {section.fields?.map((field) => (
                                <Card key={field.id} className="p-3">
                                  <div className="space-y-3">
                                    <div className="flex items-start justify-between gap-2">
                                      <div className="flex-1 grid gap-2 sm:grid-cols-2">
                                        <Input
                                          value={field.label}
                                          onChange={(e) => updateFieldInSection(section.id, field.id, { label: e.target.value })}
                                          placeholder="Field label"
                                        />
                                        <Select 
                                          value={field.type} 
                                          onValueChange={(v) => updateFieldInSection(section.id, field.id, { type: v as TemplateField['type'] })}
                                        >
                                          <SelectTrigger>
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {FIELD_TYPES.map((ft) => (
                                              <SelectItem key={ft.value} value={ft.value}>
                                                {ft.label}
                                              </SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                      </div>
                                      <Button 
                                        variant="ghost" 
                                        size="sm"
                                        onClick={() => deleteField(section.id, field.id)}
                                        className="text-muted-foreground hover:text-destructive"
                                      >
                                        <X className="h-4 w-4" />
                                      </Button>
                                    </div>
                                    
                                    <div className="flex items-center gap-4 text-sm">
                                      <label className="flex items-center gap-2">
                                        <Switch
                                          checked={field.validation?.required || false}
                                          onCheckedChange={(checked) => updateFieldInSection(section.id, field.id, { 
                                            validation: { ...field.validation, required: checked }
                                          })}
                                        />
                                        <span className="text-muted-foreground">Required</span>
                                      </label>
                                      
                                      <Input
                                        value={field.placeholder || ''}
                                        onChange={(e) => updateFieldInSection(section.id, field.id, { placeholder: e.target.value })}
                                        placeholder="Placeholder text"
                                        className="flex-1 h-8 text-sm"
                                      />
                                    </div>

                                    {['select', 'multi_select', 'radio', 'checkbox'].includes(field.type) && (
                                      <div className="space-y-2">
                                        <Label className="text-xs">Options (one per line)</Label>
                                        <Textarea
                                          value={field.options?.map(o => o.label).join('\n') || ''}
                                          onChange={(e) => {
                                            const lines = e.target.value.split('\n').filter(l => l.trim());
                                            const options = lines.map((line, i) => ({
                                              value: line.toLowerCase().replace(/\s+/g, '_'),
                                              label: line,
                                            }));
                                            updateFieldInSection(section.id, field.id, { options });
                                          }}
                                          placeholder="Option 1&#10;Option 2&#10;Option 3"
                                          rows={3}
                                          className="text-sm"
                                        />
                                      </div>
                                    )}
                                  </div>
                                </Card>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </div>
        </div>
      </ScrollArea>

      <div className="p-6 pt-4 border-t flex items-center gap-2 flex-wrap">
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <div className="flex-1" />
        {!isTemplate && editedForm?.status !== 'published' && (
          <Button variant="outline" onClick={handlePublish} disabled={isSaving}>
            <Send className="h-4 w-4 mr-2" />
            Publish
          </Button>
        )}
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save {!isTemplate && editedForm?.status !== 'published' ? 'Draft' : ''}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

// Sheet version for mobile
function FormEditorSheet(props: FormEditorProps) {
  const { open, onOpenChange, template, lenderForm } = props;
  const isTemplate = !!template;
  
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[90vh] p-0 flex flex-col">
        <SheetHeader className="p-4 pb-2 border-b">
          <SheetTitle className="flex items-center gap-2">
            {isTemplate ? (
              <><LayoutTemplate className="h-5 w-5 text-primary" /> Edit Template</>
            ) : (
              <><FileEdit className="h-5 w-5 text-primary" /> {lenderForm?.id ? 'Edit' : 'New'} Lender Form</>
            )}
          </SheetTitle>
          <SheetDescription>
            {isTemplate 
              ? 'Configure sections and fields for this base template'
              : `Form for ${lenderForm?.lenderName || 'lender'}`
            }
          </SheetDescription>
        </SheetHeader>
        <FormEditorContent {...props} onClose={() => onOpenChange(false)} />
      </SheetContent>
    </Sheet>
  );
}

// Dialog version for desktop
function FormEditorDialog(props: FormEditorProps) {
  const { open, onOpenChange, template, lenderForm } = props;
  const isTemplate = !!template;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[85vh] p-0 flex flex-col">
        <DialogHeader className="p-6 pb-4 border-b">
          <DialogTitle className="flex items-center gap-2">
            {isTemplate ? (
              <><LayoutTemplate className="h-5 w-5 text-primary" /> Edit Template</>
            ) : (
              <><FileEdit className="h-5 w-5 text-primary" /> {lenderForm?.id ? 'Edit' : 'New'} Lender Form</>
            )}
          </DialogTitle>
          <DialogDescription>
            {isTemplate 
              ? 'Configure sections and fields for this base template'
              : `Form for ${lenderForm?.lenderName || 'lender'} - ${LOAN_TYPES.find(lt => lt.id === lenderForm?.loanType)?.name}`
            }
          </DialogDescription>
        </DialogHeader>
        <FormEditorContent {...props} onClose={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  );
}
